import '../../../styles/carrousels/header-carrousel.module.scss';
import * as carrouselStyles from '../../../styles/carrousels/carrousel.module.scss';
import {GatsbyImage, getImage} from 'gatsby-plugin-image';
import React, {Fragment} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import SwiperCore, {Pagination} from 'swiper';
import CategoriesContainer from '../CategoriesContainer';
import IconContainer from '../IconContainer';
import {Link} from 'gatsby';
import _ from 'lodash';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {altNN, getDisplayLang} from '../../../utils/parsing';
import {useTranslation} from 'react-i18next';
import {toExternalUrl} from '../../../utils/utils';
import 'swiper/scss';
import 'swiper/scss/pagination';

SwiperCore.use([Pagination]);
const getSlideType = (slide) => {
  for (const key of ['event', 'news_item']) {
    if (slide[key]) {
      return key;
    }
  }
  return 'slide';
};


const propertiesMap = {
  'title': {
    'event': 'event.title', 'news_item': 'news_item.title', 'slide': 'title'
  }, 'subtitle': {
    'event': 'event.heading', 'news_item': 'news_item.heading', 'slide': 'subtitle'
  }, 'date': {
    'event': 'event.start_date', 'news_item': 'news_item.publishedAt'
  }
};


const getPropertyByNameAndObjectType = (o, prop, type) => {
  const pth = propertiesMap[prop];
  if (pth && pth[type]) {
    return _.get(o, pth[type]);
  }
  return null;
};

const folderObjecLocaletMap = {
  'event-en': 'events', 'event-fr': 'evenements', 'news_item-en': 'news', 'news_item-fr': 'actualites'
};

const getSlideUrl = (slide, type, siteURL, locale) => {
  let link = null;
  if (type === 'event') {
    const folderKey = `${type}-${locale}`;
    link = `${siteURL}/${folderObjecLocaletMap[folderKey]}/${slide.event.slug}`;
  } else if (type === 'news_item') {
    const folderKey = `${type}-${locale}`;
    link = `${siteURL}/${folderObjecLocaletMap[folderKey]}/${slide.news_item.slug}`;
  } else {
    return toExternalUrl(slide.link);
  }
  if (link) {
    return link.replace(/\/\/+/g, '/');
  }
  return null;
};

const toTypedSlide = (slide, siteURL, locale) => {
  if (!slide) return null;
  const type = getSlideType(slide);
  return {
    type,
    title: getPropertyByNameAndObjectType(slide, 'title', type),
    subtitle: getPropertyByNameAndObjectType(slide, 'subtitle', type),
    date: getPropertyByNameAndObjectType(slide, 'date', type),
    url: getSlideUrl(slide, type, siteURL, locale)
  };
};

const HeaderCarrousel = React.memo(function HeaderCarrousel ({
  items, color, siteURL, locale, isEventGuide = false
}) {
  const {t} = useTranslation();
  const paginationRef = React.useRef(null);
  const [swiper, setSwiper] = React.useState(null);
  const [value, setValue] = React.useState(0);
  const [display, setDisplay] = React.useState(false);

  React.useEffect(() => {
    setDisplay(true);
  }, []);

  const formatItem = React.useCallback((item) => {
    const displayLang = getDisplayLang(item?.event?.evt_language, locale);
    const slide = toTypedSlide(item, siteURL, locale);
    const content = (
      <>
        {slide.title && (
          <p
            className={color === 'RED'
              ? clsx(carrouselStyles.headerTitle, carrouselStyles.whiteFont)
              : clsx(carrouselStyles.headerTitle, carrouselStyles.darkFont)}
            lang={displayLang}
          >
            {slide.title}
          </p>
        )}
        {slide.subtitle && (
          <p
            className={color === 'RED'
              ? clsx(carrouselStyles.headerSubtitle, carrouselStyles.whiteFont)
              : clsx(carrouselStyles.headerSubtitle, carrouselStyles.darkFont)}
            lang={displayLang}
          >
            {slide.subtitle}
          </p>
        )}
      </>
    );
    return ({
      ...item,
      ...slide,
      content: content,
    });
  }, [siteURL, locale, color]);

  const createItems = React.useCallback(() => {
    let cpyitems;
    if (isEventGuide) {
      cpyitems = (_.uniqBy(items, 'id').map((item) => formatItem(item)));
    }
    cpyitems = (items.map((item) => formatItem(item)));
    cpyitems = cpyitems.filter((item) => (item.media?.imageFile));
    return (cpyitems);
  }, [items, isEventGuide, formatItem]);

  const itemsFormat = React.useMemo(() => (createItems()), [createItems]);
  return (
    <div
      aria-label={items?.aria_label}
      aria-roledescription="carrousel"
      className={carrouselStyles.relative}
      role="group"
    >
      {items?.length > 0 && (
        <>
          <Swiper
            breakpoints={{
              0: {
                slidesPerView: 1.02, spaceBetween: 0,
              }, 1250: {
                slidesPerView: 1.8, spaceBetween: 0,
              },
            }}
            centeredSlides
            className={clsx({[carrouselStyles.divloadHeader]: !display})}
            loop
            onSlideChange={(core) => {
              setValue(core.realIndex);
            }}
            onSwiper={(core) => setSwiper(core)}
            pagination={{
              el: paginationRef.current,
              clickable: true,
              bulletElement: 'li',
              bulletActiveClass: carrouselStyles.thumbnailsActive,
              bulletClass: carrouselStyles.thumbnails,
            }}
            slidesPerView={1.8}
            spaceBetween={10}
            speed={600}
          >
            {itemsFormat?.map((item, index) => (
              <SwiperSlide
                aria-label={item.aria_label}
                aria-roledescription="diapo"
                key={'swiper-index' + index}
                role="tabpanel"
              >
                <GatsbyImage
                  alt={altNN('')}
                  image={getImage(item.media?.imageFile)}
                  loading="eager"
                  style={{height: '450px', width: '100%'}}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            className={carrouselStyles.cadreHeader}
          >
            <div
              className={carrouselStyles.leftArrowHeader}
            >
              {display &&
                <button
                  aria-label={t('carrousel.icon.previous')}
                  onClick={() => {
                    swiper.slidePrev();
                  }}
                  onKeyDown={(e) => {
                    e.currentTarget.click();
                  }}
                  tabIndex="0"
                >
                  <IconContainer
                    color={color === 'RED' ? 'WHITE' : 'RED'}
                    icon="ARROW_LEFT_LARGE"
                    size="25"
                    svgClassname={carrouselStyles.svgIcon}
                    variant="contained"
                  />
                </button>
              }
            </div>
            <div
              className={clsx(carrouselStyles.rootContentSlider, {
                [carrouselStyles.whiteBackground]: color !== 'RED',
                [carrouselStyles.redBackground]: color === 'RED'
              })}
            >
              <div className={carrouselStyles.itemContentSlider}>
                {itemsFormat[value]?.date && (
                  <div className={carrouselStyles.containerCategorieContainer}>
                    <CategoriesContainer
                      elements={[
                        {
                          id: 1, label: new Date(itemsFormat[value]?.date)?.toLocaleDateString('fr-FR', {
                            day: 'numeric', month: 'numeric', year: 'numeric',
                            timeZone:'CET'
                          }), color: 'var(--red-color)', variant: 'contained', clickOff: true
                        },
                      ]}
                    />
                  </div>
                )}
                <div className={carrouselStyles.containerContent}>
                  <div
                    className={itemsFormat[value]?.url ? carrouselStyles.headerLink : ''}
                  >
                    {itemsFormat[value]?.url ? ((!itemsFormat[value].event && !itemsFormat[value].news_item) ? (
                      <a
                        aria-label={itemsFormat[value].content}
                        className={clsx({[carrouselStyles.linkBlank]: itemsFormat[value].new_window})}
                        href={toExternalUrl(itemsFormat[value].url)}
                        rel="noreferrer"
                        target={itemsFormat[value].new_window ? '_blank' : '_self'}
                      >
                        {itemsFormat[value].content}
                      </a>
                    ) : (
                      <Link
                        className={clsx({[carrouselStyles.linkBlank]: itemsFormat[value].new_window})}
                        target={itemsFormat[value].new_window ? '_blank' : '_self'}
                        to={toExternalUrl(itemsFormat[value].url, true)}
                      >
                        {itemsFormat[value].content}
                      </Link>
                    )) : (itemsFormat[value]?.content)}
                  </div>
                </div>
              </div>
            </div>
            <div className={carrouselStyles.rightArrowHeader}>
              {display &&
                <button
                  aria-label={t('carrousel.icon.next')}
                  onClick={() => {
                    swiper.slideNext();
                  }}
                  onKeyDown={(e) => {
                    e.currentTarget.click();
                  }}
                  tabIndex="0"
                >
                  <IconContainer
                    aria-label={t('carrousel.rightArrow')}
                    color={color === 'RED' ? 'WHITE' : 'RED'}
                    icon="ARROW_RIGHT_LARGE"
                    size="25"
                    svgClassname={carrouselStyles.svgIcon}
                    variant="contained"
                  />
                </button>
              }
            </div>
            <div className={carrouselStyles.thumbnailMargin}>
              <ul
                aria-label={t('carrousel.chips')}
                id="pagination"
                ref={paginationRef}
                style={{display: 'flex'}}
              >
              </ul>
            </div>
          </div>
        </>)}
    </div>);
});

HeaderCarrousel.propTypes = {
  color: PropTypes.oneOf(['RED', 'WHITE']),
  isEventGuide: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      media: PropTypes.shape({
        imageFile: PropTypes.object.isRequired,
        name: PropTypes.string,
        id: PropTypes.string,
      }),
      title: PropTypes.string,
      link: PropTypes.string,
      newWindow: PropTypes.bool,
      aria_label: PropTypes.string,
      event: PropTypes.shape({
        evt_language: PropTypes.string,
        title: PropTypes.string,
        heading: PropTypes.string,
        start_date: PropTypes.string,
        slug: PropTypes.string,
      }),
      news_item: PropTypes.shape({
        title: PropTypes.string,
        heading: PropTypes.string,
        publishedAt: PropTypes.string,
        slug: PropTypes.string,
      }),
    })
  ),
  locale: PropTypes.string,
  siteURL: PropTypes.string,
};

export default HeaderCarrousel;
